import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { createONDCIssueAction } from "src/actions/issuesActions";
import { useLocation } from "react-router-dom";


const IssueForm = ({ serverTypes }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const location = useLocation();
  const { row } = location.state || {};
  const itemIds = row.order.items.map((item) => item?.id);
  const itemId0 = itemIds.length ? itemIds[0]: "";
// console.log(itemIds);
  const [preferredServerType, setPreferredServerType] = useState(serverTypes[0]);

  const [formData, setFormData] = useState({
    ondcOrderId: row?.ondcOrderId || "",
    itemId: itemId0,
    expectedResponseTime: "",
    expectedResolutionTime: "",
    level: "",
    descriptionShort: "",
    descriptionLong: "",
    images: [],
  });

  const [status, setStatus] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({ ...prev, images: [...prev.images, ...files] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {      
      await dispatch(createONDCIssueAction(formData,preferredServerType));
      enqueueSnackbar("Issue Created Successfully", { variant: "success" });
      setStatus({ success: true });
      setErrors({});
    } catch (err) {
      setErrors({ submit: err.message });
      setStatus({ success: false });
      enqueueSnackbar("Failed to Create Issue", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, maxWidth: 700, mx: "auto" }}>
      <Typography variant="h6" gutterBottom>
        Create Issue
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Autocomplete
            options={serverTypes}
            value={preferredServerType}
            onChange={(event, newValue) => {
              if (newValue) {
                setPreferredServerType(newValue);                
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Server Type" variant="outlined" />
            )}
            disableClearable // prevent clearing the field
          />
        </Grid>

        {/* Order ID */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Order ID"
            name="ondcOrderId"
            value={formData.ondcOrderId}
            onChange={handleChange}
          />
        </Grid>

        {/* Item ID */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Item ID"
            name="itemId"
            value={formData.itemId}
            onChange={handleChange}
          />
        </Grid>

        {/* Expected Response Time */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Expected Response Time (hrs)"
            name="expectedResponseTime"
            value={formData.expectedResponseTime}
            onChange={handleChange}
            inputProps={{ min: 1, max: 24 }}
          />
        </Grid>

        {/* Expected Resolution Time */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Expected Resolution Time (days)"
            name="expectedResolutionTime"
            value={formData.expectedResolutionTime}
            onChange={handleChange}
            inputProps={{ min: 1, max: 7 }}
          />
        </Grid>

        {/* Level */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Level (Issue/Grievance/Dispute)"
            name="level"
            value={formData.level}
            onChange={handleChange}
          />
        </Grid>

        {/* Issue Description Short */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Issue Description (Short)"
            name="descriptionShort"
            value={formData.descriptionShort}
            onChange={handleChange}
          />
        </Grid>

        {/* Issue Description Long */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Issue Description (Long)"
            name="descriptionLong"
            value={formData.descriptionLong}
            onChange={handleChange}
          />
        </Grid>

        {/* Image Upload */}
        <Grid item xs={12}>
          <Button variant="outlined" component="label">
            Upload Images
            <input type="file" hidden multiple onChange={handleImageUpload} />
          </Button>

          <Box mt={1}>
            {formData.images.length > 0 && (
              <Typography variant="body2">
                {formData.images.length} image(s) selected
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Submit */}
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Create"}
          </Button>
        </Grid>

        {errors.submit && (
          <Grid item xs={12}>
            <Typography color="error">{errors.submit}</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default IssueForm;
